/**
 * Everything currently presentable, as one ordered list of chips.
 *
 * Split out of `useStageContent` so the ordering rules can be tested without a
 * renderer — the hook around it only decides which of these the viewer is
 * looking at, and that half already lives in `stage-selection`.
 *
 * ## Why documents and videos are lists
 *
 * They were a single slot each, and the room's own record — the shared-media
 * stack in `useSharedMedia` — has always been a stack that holds every live
 * presentation. `MeetingScreen` collapsed it to its top element before handing
 * it over, so a second document presented by a second participant did not add
 * a chip, it REPLACED the only one there was. Nothing was lost: the first
 * document was still on the room's stack and still shared, it simply had no
 * chip to be reached by, and the panel's Present button was the only way back
 * to it. Screen shares never had this problem because they always arrived as a
 * list.
 */

export type StageItemKind = 'screen' | 'whiteboard' | 'document' | 'video'

export type StageItem = {
  /**
   * Identifies the item to THIS client. Screen shares key off the local
   * `shares` list, where your own is literally `'local'`, so these are not
   * comparable between machines — see `syncId` for the portable one.
   */
  key: string
  kind: StageItemKind
  /** What the chip reads. Already localised by the caller. */
  label: string
  /**
   * The same item's name on every client. Your own screen share is `'local'`
   * to you and your peerId to everyone else, so a host syncing the room to
   * `'local'` would point each peer at their own screen instead of the host's.
   * Sync therefore travels as this, and each receiver resolves it locally.
   */
  syncId: string
  isLocal: boolean
}

export type StageSources = {
  /** Screen shares, in the order the stage already had them. */
  screens: { key: string; name: string; isLocal: boolean; peerId?: string }[]
  whiteboardActive: boolean
  whiteboardLabel: string
  /** Every document on the room's stage, oldest first. */
  documents: { id: string; name: string }[]
  /** Every video on the room's stage, oldest first. */
  videos: { id: string; name: string }[]
  localPeerId: string | null | undefined
}

/** The chip key for one presented document or video. */
export function mediaStageKey(kind: 'document' | 'video', id: string): string {
  return `${kind}:${id}`
}

export function buildStageItems({
  screens,
  whiteboardActive,
  whiteboardLabel,
  documents,
  videos,
  localPeerId,
}: StageSources): StageItem[] {
  const out: StageItem[] = []

  // Screens first, and among them in the order the caller supplied, so the
  // chips do not reshuffle under the pointer when someone starts sharing.
  for (const s of screens) {
    const portable = s.isLocal ? (localPeerId ?? 'local') : (s.peerId ?? s.key)
    out.push({
      key: s.key,
      kind: 'screen',
      label: s.name,
      syncId: `screen:${portable}`,
      isLocal: s.isLocal,
    })
  }

  if (whiteboardActive) {
    // One board per room, so its identity is the kind itself.
    out.push({
      key: 'whiteboard',
      kind: 'whiteboard',
      label: whiteboardLabel,
      syncId: 'whiteboard',
      isLocal: false,
    })
  }

  // Stack order, which is the order they were presented in. Stable as long as
  // nothing is closed, so a chip never moves out from under the pointer
  // because somebody else started presenting.
  for (const doc of documents) {
    out.push({
      key: mediaStageKey('document', doc.id),
      kind: 'document',
      label: doc.name,
      syncId: mediaStageKey('document', doc.id),
      isLocal: false,
    })
  }

  for (const vid of videos) {
    out.push({
      key: mediaStageKey('video', vid.id),
      kind: 'video',
      label: vid.name,
      syncId: mediaStageKey('video', vid.id),
      isLocal: false,
    })
  }

  return out
}
